import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Filter, X, Loader2 } from 'lucide-react';
import apiService from '../../services/api';
import { CompanyNote, CreateCompanyNote } from '../../types';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';

interface CompanyNotesPanelProps {
  companyId: number;
  companyName: string;
}

const CompanyNotesPanel: React.FC<CompanyNotesPanelProps> = ({
  companyId,
  companyName
}) => {
  const [notes, setNotes] = useState<CompanyNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterType, setFilterType] = useState<'All' | 'General' | 'Support' | 'Billing' | 'Technical'>('All');
  const [newNote, setNewNote] = useState<CreateCompanyNote>({
    note: '',
    noteType: 'General',
    isInternal: true
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<CompanyNote | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadNotes();
    }
  }, [companyId]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const response = await apiService.getCompanyNotes(companyId);
      console.log('Notes response:', response);
      if (response.success && response.data) {
        console.log('Notes data:', response.data);
        setNotes(Array.isArray(response.data) ? response.data : []);
      } else {
        console.warn('Notes response not successful:', response);
        setNotes([]);
      }
    } catch (error) {
      console.error('Error loading notes:', error);
      toast.error('Failed to load company notes');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.note.trim()) {
      toast.error('Note content is required');
      return;
    }

    try {
      const response = await apiService.addCompanyNote(companyId, newNote);
      if (response.success && response.data) {
        toast.success('Note added successfully');
        setNewNote({ note: '', noteType: 'General', isInternal: true });
        setShowAddForm(false);
        loadNotes();
      } else {
        toast.error(response.message || 'Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleDeleteClick = (note: CompanyNote) => {
    setNoteToDelete(note);
    setShowDeleteConfirm(true);
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteCompanyNote(companyId, noteToDelete.id);
      if (response.success) {
        toast.success('Note deleted successfully');
        setShowDeleteConfirm(false);
        setNoteToDelete(null);
        loadNotes();
      } else {
        toast.error(response.message || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    } finally {
      setDeleting(false);
    }
  };

  const getNoteTypeColor = (type: string) => {
    switch (type) {
      case 'Support':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'Billing':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'Technical':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const filteredNotes = filterType === 'All' 
    ? notes 
    : notes.filter(note => note.noteType === filterType);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Company Notes - {companyName}
        </h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="btn-primary inline-flex items-center px-3 py-2"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Note
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as any)}
          className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="All">All Types</option>
          <option value="General">General</option>
          <option value="Support">Support</option>
          <option value="Billing">Billing</option>
          <option value="Technical">Technical</option>
        </select>
      </div>

      {/* Add Note Form */}
      {showAddForm && (
        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Note Type
              </label>
              <select
                value={newNote.noteType}
                onChange={(e) => setNewNote({ ...newNote, noteType: e.target.value as any })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="General">General</option>
                <option value="Support">Support</option>
                <option value="Billing">Billing</option>
                <option value="Technical">Technical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Note
              </label>
              <textarea
                value={newNote.note}
                onChange={(e) => setNewNote({ ...newNote, note: e.target.value })}
                rows={4}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="Enter note content..."
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isInternal"
                checked={newNote.isInternal}
                onChange={(e) => setNewNote({ ...newNote, isInternal: e.target.checked })}
                className="h-4 w-4 text-gray-900 focus:ring-gray-500 border-gray-300 rounded"
              />
              <label htmlFor="isInternal" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Internal note (only visible to system admins)
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddNote}
                className="btn-primary px-4 py-2 text-sm"
              >
                Save Note
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewNote({ note: '', noteType: 'General', isInternal: true });
                }}
                className="btn-secondary px-4 py-2 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes List */}
      <div className="space-y-3">
        {filteredNotes.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No notes found
          </div>
        ) : (
          filteredNotes.map((note) => (
            <div
              key={note.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNoteTypeColor(note.noteType)}`}>
                      {note.noteType}
                    </span>
                    {note.isInternal && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                        Internal
                      </span>
                    )}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(note.createdDate).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-2">
                    {note.note}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Created by {note.createdByName}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteClick(note)}
                  className="ml-4 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  title="Delete note"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && noteToDelete && (
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setNoteToDelete(null);
          }}
          title="Delete Note"
          maxWidth="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete this note?
            </p>
            <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md border border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {noteToDelete.note.substring(0, 100)}{noteToDelete.note.length > 100 ? '...' : ''}
              </p>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setNoteToDelete(null);
                }}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteNote}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Note'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CompanyNotesPanel;

